import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Avatar, Tooltip, useMediaQuery } from "@material-ui/core";
import {
  KeyboardArrowUp,
  KeyboardArrowDown,
  PostAdd,
  Close,
} from "@material-ui/icons";
import styles from "./Style.module.css";
import useMessagingHook from "../../views/Messaging/List/Messaging.hook";
import FloatingChatWindow from "./FloatingChatWindow.component";
import StartNewChat from "./StartNewChat.component";
import EventEmitter from "../../libs/Events.utils";
import noDataImg from "../../assets/img/ic_search_empty@2x.png";
import ChatAvatarItem from "./ChatAvatarItem.component";

const TABS = {
  ALL: "ALL",
  DIRECT: "DIRECT",
  GROUPS: "GROUPS",
};

function FloatingChat() {
  const isDesktop = useMediaQuery("(min-width: 1024px)");
  const [isDockOpen, setIsDockOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(TABS.ALL);
  const [searchQuery, setSearchQuery] = useState("");
  const [isStartNewChatMode, setIsStartNewChatMode] = useState(false);
  const [openChats, setOpenChats] = useState([]);
  const [isHidden, setIsHidden] = useState(() => {
    return localStorage.getItem("floating_chat_hidden") === "true";
  });
  const [windowSize, setWindowSize] = useState({
    width: typeof window !== "undefined" ? window.innerWidth : 1280,
    height: typeof window !== "undefined" ? window.innerHeight : 768,
  });

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const cardLayout = useMemo(() => {
    const numCards = 1 + (openChats.length || 0); // Main Dock + all open chat windows
    const rightMargin = 20;
    const leftMargin = 16;
    const availableWidth = Math.max(800, windowSize.width - rightMargin - leftMargin);
    const gap = numCards >= 4 ? 8 : 12;
    const idealWidth = 330;
    const maxFitWidth = Math.floor((availableWidth - (numCards - 1) * gap) / numCards);
    const cardWidth = Math.min(idealWidth, Math.max(225, maxFitWidth));

    // Dynamic screen-aware Height (`Widgets ki heights bhi screen size ke according adjuest honi chahiye`)
    const topMargin = 70; // Keep space at top for navbar and ensure headers/close buttons never overflow
    const idealHeight = 520;
    const minHeight = 320; // Minimum usable height for chat history + input box
    const maxAvailableHeight = Math.max(minHeight, windowSize.height - topMargin);
    const cardHeight = Math.min(idealHeight, maxAvailableHeight);

    return {
      cardWidth,
      cardHeight,
      gap,
      rightMargin,
    };
  }, [windowSize, openChats.length]);

  const {
    chatInfoSingle = [],
    groupChatInfo = [],
    fetchChatInfo,
  } = useMessagingHook({});

  const totalUnread = useMemo(() => {
    let sum = 0;
    (chatInfoSingle || []).forEach((c) => {
      sum += c?.unread_count || 0;
    });
    (groupChatInfo || []).forEach((c) => {
      sum += c?.unread_count || 0;
    });
    return sum;
  }, [chatInfoSingle, groupChatInfo]);

  const handleOpenChat = useCallback((dm) => {
    if (!dm) return;
    setOpenChats((prev) => {
      const existingIndex = prev.findIndex(
        (c) =>
          (c?.id && dm?.id && c.id === dm.id) ||
          (c?.conversation_id &&
            dm?.conversation_id &&
            c.conversation_id === dm.conversation_id)
      );

      if (existingIndex >= 0) {
        // Unminimize if already open
        const updated = [...prev];
        updated[existingIndex] = { ...updated[existingIndex], isMinimized: false };
        return updated;
      }

      const newChat = { ...dm, isMinimized: false };
      if (prev.length >= 3) {
        // Remove the oldest (first) chat window to keep max 3 open
        return [...prev.slice(1), newChat];
      }
      return [...prev, newChat];
    });
  }, []);

  const handleToggleMinimizeChat = useCallback((dm) => {
    setOpenChats((prev) =>
      prev.map((c) => {
        const isMatch =
          (c?.id && dm?.id && c.id === dm.id) ||
          (c?.conversation_id &&
            dm?.conversation_id &&
            c.conversation_id === dm.conversation_id);
        if (isMatch) {
          return { ...c, isMinimized: !c.isMinimized };
        }
        return c;
      })
    );
  }, []);

  const handleCloseChat = useCallback((dm) => {
    setOpenChats((prev) =>
      prev.filter((c) => {
        const isMatch =
          (c?.id && dm?.id && c.id === dm.id) ||
          (c?.conversation_id &&
            dm?.conversation_id &&
            c.conversation_id === dm.conversation_id);
        return !isMatch;
      })
    );
  }, []);

  useEffect(() => {
    const handleOpenFromEvent = (chatData) => {
      handleOpenChat(chatData);
    };
    const handleToggleDockFromEvent = () => {
      setIsDockOpen((prev) => !prev);
    };

    EventEmitter.subscribe(
      EventEmitter.OPEN_FLOATING_CHAT,
      handleOpenFromEvent
    );
    EventEmitter.subscribe(
      EventEmitter.TOGGLE_FLOATING_CHAT,
      handleToggleDockFromEvent
    );

    return () => {
      EventEmitter.unsubscribe(EventEmitter.OPEN_FLOATING_CHAT);
      EventEmitter.unsubscribe(EventEmitter.TOGGLE_FLOATING_CHAT);
    };
  }, [handleOpenChat]);

  const toggleHidden = useCallback(() => {
    setIsHidden(prev => {
      const next = !prev;
      localStorage.setItem("floating_chat_hidden", next);
      return next;
    });
  }, []);

  const filteredChatList = useMemo(() => {
    let list = [];
    if (activeTab === TABS.ALL) {
      list = [...(chatInfoSingle || []), ...(groupChatInfo || [])];
      list.sort((a, b) => (b.last_message_time || 0) - (a.last_message_time || 0));
    } else if (activeTab === TABS.DIRECT) {
      list = [...(chatInfoSingle || [])];
    } else if (activeTab === TABS.GROUPS) {
      list = [...(groupChatInfo || [])];
    }

    if (!searchQuery.trim()) return list;

    const query = searchQuery.trim().toLowerCase();
    return list.filter((item) => {
      const label = item?.meta_data?.label || item?.name || "";
      const description = item?.meta_data?.description || item?.code || "";
      return (
        label.toLowerCase().includes(query) ||
        description.toLowerCase().includes(query)
      );
    });
  }, [activeTab, chatInfoSingle, groupChatInfo, searchQuery]);

  // Requirement: only visible on desktop screens (>= 1024px)
  if (!isDesktop) {
    return null;
  }

  return (
    <>
      {/* Restore Floating Chat FAB */}
      <div
        onClick={toggleHidden}
        style={{
          position: "fixed",
          bottom: "20px",
          right: "0",
          zIndex: 9999,
          background: "linear-gradient(265deg, #7467f0, #2896e9)",
          borderRadius: "8px 0 0 8px",
          width: "48px",
          height: "48px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          boxShadow: "-4px 0px 12px rgba(0,0,0,0.15)",
          color: "white",
          transform: isHidden ? "translateX(0)" : "translateX(100%)",
          opacity: isHidden ? 1 : 0,
          pointerEvents: isHidden ? "auto" : "none",
          transition: "all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)"
        }}
      >
        <img
          src={require("../../assets/img/ic_new_chat.png")}
          alt="Chat"
          style={{ width: 24, height: 24, filter: "brightness(0) invert(1)" }}
        />
      </div>

      {/* Individual Open Chat Windows (Up to 3) */}
      {openChats.map((chat, idx) => {
        const rightPos =
          cardLayout.rightMargin +
          (idx + 1) * (cardLayout.cardWidth + cardLayout.gap);
        return (
          <FloatingChatWindow
            key={chat?.id || chat?.conversation_id || idx}
            dm={chat}
            positionIndex={idx}
            cardWidth={cardLayout.cardWidth}
            cardHeight={cardLayout.cardHeight}
            rightPos={rightPos}
            onClose={handleCloseChat}
            onToggleMinimize={handleToggleMinimizeChat}
            fetchChatInfo={fetchChatInfo}
            isHidden={isHidden}
          />
        );
      })}

      {/* Main Messaging Dock at Bottom Right */}
      <div
        className={`${styles.dockContainer} ${
          isDockOpen ? styles.dockContainerOpen : styles.dockContainerMinimized
        }`}
        style={{
          width: `${cardLayout.cardWidth}px`,
          height: isDockOpen ? `${cardLayout.cardHeight}px` : "48px",
          right: `${cardLayout.rightMargin}px`,
          transform: isHidden ? "translateY(150%)" : "translateY(0)",
          opacity: isHidden ? 0 : 1,
          pointerEvents: isHidden ? "none" : "auto",
          transition: "transform 0.4s ease-in-out, opacity 0.4s ease-in-out, height 0.3s ease",
        }}
      >
        {/* Dock Header */}
        <div
          className={styles.dockHeader}
          onClick={() => setIsDockOpen((prev) => !prev)}
        >
          <div className={styles.dockHeaderTitle}>
            <Avatar
              src={require("../../assets/img/ic_new_chat.png")}
              style={{ width: 28, height: 28 }}
            />
            <span>Messaging</span>
            {totalUnread > 0 && (
              <div className={styles.unreadBadge}>{totalUnread}</div>
            )}
          </div>

          <div className={styles.headerActions}>
            <Tooltip title="Start New Chat">
              <div
                className={styles.iconBtn}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!isDockOpen) setIsDockOpen(true);
                  setIsStartNewChatMode((prev) => !prev);
                }}
              >
                <PostAdd fontSize="small" />
              </div>
            </Tooltip>
            <Tooltip title={isDockOpen ? "Minimize" : "Expand"}>
              <div className={styles.iconBtn}>
                {isDockOpen ? (
                  <KeyboardArrowDown fontSize="small" />
                ) : (
                  <KeyboardArrowUp fontSize="small" />
                )}
              </div>
            </Tooltip>
            <Tooltip title="Close Completely">
              <div
                className={styles.iconBtn}
                onClick={(e) => {
                  e.stopPropagation();
                  toggleHidden();
                }}
              >
                <Close fontSize="small" />
              </div>
            </Tooltip>
          </div>
        </div>

        {/* Dock Body */}
        {isDockOpen && (
          <div className={styles.dockBody}>
            {isStartNewChatMode ? (
              <StartNewChat
                onSelectUser={(emp) => {
                  handleOpenChat(emp);
                  setIsStartNewChatMode(false);
                }}
                onBack={() => setIsStartNewChatMode(false)}
              />
            ) : (
              <>
                {/* Search Bar */}
                <div className={styles.searchWrap}>
                  <input
                    type="text"
                    className={styles.searchInput}
                    placeholder="Search existing conversations..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>

                {/* Tabs */}
                <div className={styles.tabsWrap}>
                  <div
                    className={`${styles.tabItem} ${
                      activeTab === TABS.ALL ? styles.activeTab : ""
                    }`}
                    onClick={() => setActiveTab(TABS.ALL)}
                  >
                    All
                  </div>
                  <div
                    className={`${styles.tabItem} ${
                      activeTab === TABS.DIRECT ? styles.activeTab : ""
                    }`}
                    onClick={() => setActiveTab(TABS.DIRECT)}
                  >
                    Direct
                  </div>
                  <div
                    className={`${styles.tabItem} ${
                      activeTab === TABS.GROUPS ? styles.activeTab : ""
                    }`}
                    onClick={() => setActiveTab(TABS.GROUPS)}
                  >
                    Groups
                  </div>
                </div>

                {/* Chat Conversations List */}
                <div className={styles.chatList}>
                  {filteredChatList.length > 0 ? (
                    filteredChatList.map((dm, idx) => {
                      const profile = dm?.meta_data || {
                        label: dm?.name || "Conversation",
                        description: dm?.code || "",
                        image: dm?.image || "",
                        initials: dm?.initials || "",
                        display_type: dm?.display_type || "",
                      };

                      return (
                        <div
                          key={`${dm?.conversation_id || dm?.id}_${idx}`}
                          className={styles.chatListItem}
                          onClick={() => handleOpenChat(dm)}
                        >
                          <div className={styles.chatItemLeft}>
                            <ChatAvatarItem
                              image={profile?.image}
                              initials={profile?.initials}
                              label={profile?.label}
                              displayType={profile?.display_type}
                              isGroup={dm?.type === "GROUP" || profile?.is_group}
                              size={38}
                            />
                            <div className={styles.chatInfo}>
                              <div className={styles.chatTitle}>
                                {profile?.label}
                              </div>
                              <div className={styles.chatDes}>
                                {profile?.description ||
                                  (dm?.type === "GROUP" ? "Group Chat" : "Direct Message")}
                              </div>
                            </div>
                          </div>

                          {dm?.unread_count > 0 && (
                            <div className={styles.unreadBadge}>
                              {dm.unread_count}
                            </div>
                          )}
                        </div>
                      );
                    })
                  ) : (
                    <div className={styles.emptyState}>
                      <img
                        src={noDataImg}
                        height={80}
                        alt="Empty"
                        style={{ opacity: 0.7 }}
                      />
                      <b style={{ color: "#1f2328" }}>No Conversations Found</b>
                      <span>
                        {searchQuery
                          ? "Try searching with a different keyword"
                          : "Start a new conversation by clicking '+' above"}
                      </span>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </>
  );
}

export default FloatingChat;
